import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { COLORS, FONTS, STATUS_COLORS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';

import { tokens } from '@design';
import { getTextAlign } from '../utils/rtl';
interface ProgressBarProps {
  value: number;
  maxValue?: number;
  showLabel?: boolean;
  height?: number;
  color?: 'success' | 'warning' | 'danger' | 'primary' | 'secondary';
  backgroundColor?: string;
}

export default function ProgressBar({
  value,
  maxValue = 100,
  showLabel = true,
  height = 8,
  color,
  backgroundColor,
}: ProgressBarProps) {
  const { colors, isDark } = useTheme();

  // Use theme-aware background color if not provided
  const trackBgColor = backgroundColor || (isDark ? colors.backgroundAlt : COLORS.grayLight);
  const percentage = Math.min(Math.max((value / maxValue) * 100, 0), 100);

  // Auto-determine color based on percentage if not provided
  const getColor = () => {
    if (color) {
      if (color === 'primary') return COLORS.primary;
      if (color === 'secondary') return COLORS.secondary;
      return STATUS_COLORS[color];
    }

    if (percentage >= 80) return COLORS.success;
    if (percentage >= 50) return COLORS.warning;
    return COLORS.danger;
  };

  return (
    <View style={styles.container}>
      <View style={[styles.track, { height, backgroundColor: trackBgColor }]}>
        <View
          style={[
            styles.fill,
            {
              width: `${percentage}%`,
              backgroundColor: getColor(),
              height,
            },
          ]}
        />
      </View>
      {showLabel && (
        <Text style={[styles.label, { color: getColor() }]}>
          %{percentage.toFixed(2)}
        </Text>
      )}
    </View>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  container: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
  },
  track: {
    flex: 1,
    borderRadius: tokens.radii.absolute["radius-4"],
    overflow: 'hidden',
  },
  fill: {
    borderRadius: tokens.radii.absolute["radius-4"],
  },
  label: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontWeight: '600',
    fontFamily: FONTS.numbers,
    marginEnd: tokens.spacing.absolute["2"],
    minWidth: 45,
    textAlign: getTextAlign(isRTL),
  },
});

const styles = getStyles(true);
